import * as React from 'react';
import MediaQuery from 'react-responsive';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { getTravellerName, MOBILE_MIN_WIDTH } from '../../../utils';
import { CheckoutOrderFragment, OrderStatus } from '@websky/graphql';
import { useToggleable } from '../../../hooks';

import { Included, Person, TicketFilled, WarningRounded } from '../../../Icons';
import { useTranslation } from 'react-i18next';
import WaitingPaymentText from '../WaitingPaymentText/WaitingPaymentText';
import FlightInfoCardControls from '../FlightInfoCardControls/FlightInfoCardControls';
import Button from '../../../Button';
import { useFlightInfoCardControls } from '../../hooks';
import { CancelOrderConfirm } from '../../../__fsd__/features/cancel-order';

interface Props {
	order: CheckoutOrderFragment;
	onPassengerDetails: () => void;
	onDownloadTickets: () => void;
}

const FlightInfoCardHeader: React.FC<Props> = ({ order, onPassengerDetails, onDownloadTickets }) => {
	const { FlightInfoCardHeader: css } = useTheme('FlightInfoCard');
	const { isOpen, close, open } = useToggleable(false);

	const { t } = useTranslation('FlightInfoCard');

	const controls = useFlightInfoCardControls(order);

	return (
		<header className={cn(css.header)}>
			<h2 className={cn(css.header__title)}>
				{t('Booking')} № {order.locator}
			</h2>
			<div className={cn(css.header__info)}>
				<div className={cn(css.header__infoItem)}>
					{order.status === OrderStatus.Booked && (
						<p className={cn(css.header__text, css.header__payment)}>
							<span className={cn(css.header__textIcon)}>{WarningRounded}</span>
							<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
								№ {order.locator}
								<br />
							</MediaQuery>
							<WaitingPaymentText timelimit={order.timelimit} />
						</p>
					)}

					{order.status === OrderStatus.Confirmed && (
						<p className={cn(css.header__text, css.header__payment_confirmed)}>
							<div className={css.info__wrapper}>
								<span className={cn(css.header__textIcon)}>{Included}</span>
								<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>№ {order.locator}, </MediaQuery>
								{t('Confirmed')}
							</div>

							{!!controls.length && (
								<div className={css.button__wrapper}>
									<Button className={cn(css.header__infoButton)} variant="text" onClick={open}>
										{t('Manage order')}
									</Button>

									<FlightInfoCardControls
										controls={controls}
										isOpened={isOpen}
										onClose={close}
										outsideClickIgnoreClass={cn(css.header__infoButton)}
									/>
								</div>
							)}
						</p>
					)}

					{(order.status === OrderStatus.Cancelled || order.status === OrderStatus.Problematic) && (
						<p
							className={cn(css.header__text, css.header__payment_cancelled, {
								[css.header__payment_problematic]: order.status === OrderStatus.Problematic
							})}
						>
							<span className={cn(css.header__textIcon)}>{WarningRounded}</span>
							<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>№ {order.locator}, </MediaQuery>
							{t(order.status)}
						</p>
					)}
				</div>
				<div className={cn(css.header__infoItem)}>
					<p className={cn(css.header__text)}>
						<span className={cn(css.header__textIcon)}>{Person}</span>

						<span>
							{order.travellers.map((traveller, index) => (
								<span className={css.header__traveller} key={index}>
									{getTravellerName(traveller)}
								</span>
							))}
						</span>

						<Button className={cn(css.header__infoButton)} variant="text" onClick={onPassengerDetails}>
							{t('Details')}
						</Button>
					</p>
				</div>
				<div className={cn(css.header__infoItem)}>
					{order.status === OrderStatus.Confirmed && (
						<p className={cn(css.header__text)}>
							<span className={cn(css.header__textIcon)}>{TicketFilled}</span>
							{t('Tickets and documents')}
							<Button className={cn(css.header__infoButton)} variant="text" onClick={onDownloadTickets}>
								{t('Download')}
							</Button>
						</p>
					)}
				</div>
				{order.status === OrderStatus.Booked && (
					<CancelOrderConfirm orderId={order.id} className={css.header__cancel} />
				)}
			</div>
		</header>
	);
};

export default FlightInfoCardHeader;
